import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { staffAPI } from '../../api/staff';
import { useAuth } from '../../context/AuthContext';
import UserAvatar from '../../components/UserAvatar';
import { Plus, Search, X, Edit2, Eye, LayoutGrid, List, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import ArchiveButton from '../../components/ArchiveButton';

function StaffModal({ staff, onSave, onClose }) {
  const [form, setForm] = useState(staff || {
    first_name: '', last_name: '', position: '', phone: '', email: '', status: 'activo', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy">
          <h3 className="font-bold text-white">{staff ? 'Editar Personal' : 'Nuevo Personal'}</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="f-nombres" className="soem-label block mb-1">Nombre(s) *</label>
              <input id="f-nombres" className={inputCls} value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} required />
            </div>
            <div>
              <label htmlFor="f-apellidos" className="soem-label block mb-1">Apellidos *</label>
              <input id="f-apellidos" className={inputCls} value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} required />
            </div>
          </div>
          <div>
            <label htmlFor="f-puesto-habitual" className="soem-label block mb-1">Puesto habitual</label>
            <input id="f-puesto-habitual" className={inputCls} value={form.position || ''} onChange={e => setForm({...form, position: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="f-teléfono" className="soem-label block mb-1">Teléfono</label>
              <input id="f-teléfono" className={inputCls} value={form.phone || ''} onChange={e => setForm({...form, phone: e.target.value})} />
            </div>
            <div>
              <label htmlFor="f-email" className="soem-label block mb-1">Email</label>
              <input id="f-email" type="email" className={inputCls} value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} />
            </div>
          </div>
          <div>
            <label htmlFor="f-status" className="soem-label block mb-1">Status</label>
            <select id="f-status" className={inputCls} value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={saving} className="soem-btn-primary flex-1 py-2.5 text-sm disabled:opacity-60">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
            <button type="button" onClick={onClose} className="soem-btn-secondary flex-1 py-2.5 text-sm">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function StaffDirectory() {
  const { isAdmin, isLider } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [filters, setFilters] = useState({ q: '', status: '' });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      const r = await staffAPI.list(params);
      setStaff((r.data.items || []).filter(i => !i.is_archived));
    } catch { toast.error('Error al cargar personal'); }
    finally { setLoading(false); }
  }, [filters.status]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    if (modal?.id) {
      await staffAPI.update(modal.id, form);
      toast.success('Personal actualizado');
    } else {
      await staffAPI.create(form);
      toast.success('Personal creado');
    }
    load();
  };

  const filtered = filters.q
    ? staff.filter(s => {
        const q = filters.q.toLowerCase();
        return (s.full_name || '').toLowerCase().includes(q) ||
               (s.position || '').toLowerCase().includes(q) ||
               (s.phone || '').toLowerCase().includes(q) ||
               (s.email || '').toLowerCase().includes(q);
      })
    : staff;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="staff-directory">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Directorio de Personal Operativo</h1>
          <p className="text-slate-500 text-sm mt-1">{staff.length} personas registradas</p>
        </div>
        {(isAdmin || isLider) && (
          <button onClick={() => navigate('/dashboard/staff/nuevo')} className="soem-btn-primary px-4 py-2 text-sm flex items-center gap-2" data-testid="new-staff-btn">
            <Plus size={16} /> Nuevo Personal
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="soem-card p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="pl-9 pr-4 py-2 border border-slate-300 rounded-sm text-sm w-full focus:outline-none focus:ring-2 focus:ring-soem-primary"
            placeholder="Buscar por nombre, puesto, teléfono o correo..." value={filters.q}
            onChange={e => setFilters(f => ({...f, q: e.target.value}))} />
        </div>
        <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
          value={filters.status} onChange={e => setFilters(f => ({...f, status: e.target.value}))}>
          <option value="">Todos los estados</option>
          <option value="activo">Activos</option>
          <option value="inactivo">Inactivos</option>
        </select>
        {/* View mode toggle */}
        <div className="flex items-center gap-1 border border-slate-300 rounded-sm p-1">
          <button onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:text-slate-700'}`}
            data-testid="view-grid-btn">
            <LayoutGrid size={16} />
          </button>
          <button onClick={() => setViewMode('list')}
            className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:text-slate-700'}`}
            data-testid="view-list-btn">
            <List size={16} />
          </button>
        </div>
      </div>

      {/* Cards or List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(3)].map((_,i) => <div key={i} className="soem-card h-40 animate-pulse bg-slate-100" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="soem-card p-12 text-center">
          <p className="text-slate-400">No hay personal registrado.</p>
          {(isAdmin||isLider) && <button onClick={() => setModal({})} className="soem-btn-primary px-5 py-2 text-sm mt-4 inline-flex items-center gap-2"><Plus size={14}/> Crear</button>}
        </div>
      ) : viewMode === 'list' ? (
        /* List View */
        <div className="soem-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="soem-table-header">
                <th className="px-4 py-3 text-left">Código</th>
                <th className="px-4 py-3 text-left">Nombre</th>
                <th className="px-4 py-3 text-left">Puesto</th>
                <th className="px-4 py-3 text-left">Teléfono</th>
                <th className="px-4 py-3 text-left">Correo</th>
                <th className="px-4 py-3 text-left">Estatus</th>
                <th className="px-4 py-3 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} className="soem-table-row hover:bg-blue-50/40">
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{s.staff_code}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <UserAvatar user={{ id: s.id, first_name: s.first_name, last_name: s.last_name, profile_photo: s.photo }} size="sm" />
                      <span className="font-semibold text-soem-navy">{s.full_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{s.position || '—'}</td>
                  <td className="px-4 py-3 text-slate-500">
                    {s.phone ? (
                      <span className="flex items-center gap-1"><Phone size={11} className="text-slate-400" /> {s.phone}</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {s.email ? (
                      <span className="flex items-center gap-1"><Mail size={11} className="text-slate-400" /> {s.email}</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.status === 'activo' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {(isAdmin||isLider) && (
                      <button onClick={() => navigate(`/dashboard/staff/${s.id}/editar`)} className="soem-btn-secondary px-2 py-1 text-xs flex items-center gap-1">
                        <Edit2 size={11}/> Editar
                      </button>
                    )}
                    {isAdmin && <ArchiveButton entity="staff" id={s.id} name={s.name || `${s.first_name || ''} ${s.last_name || ''}`} onArchived={load} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(s => {
            const avatarUser = { id: s.id, first_name: s.first_name, last_name: s.last_name, name: s.full_name, profile_photo: s.photo };
            return (
              <div key={s.id} className="soem-card p-5 hover:shadow-md transition-shadow" data-testid={`staff-card-${s.id}`}>
                <div className="flex items-start gap-4">
                  <UserAvatar user={avatarUser} size="lg" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-mono text-slate-400">{s.staff_code}</p>
                        <h3 className="font-bold text-soem-navy text-base">{s.full_name}</h3>
                        {s.position && <p className="text-sm text-slate-500">{s.position}</p>}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.status === 'activo' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {s.status}
                      </span>
                    </div>
                    {s.phone && <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Phone size={10} /> {s.phone}</p>}
                    {s.email && <p className="text-xs text-slate-400 flex items-center gap-1"><Mail size={10} /> {s.email}</p>}
                    {s.events_count !== undefined && (
                      <p className="text-xs text-slate-400">{s.events_count} evento(s)</p>
                    )}
                  </div>
                </div>
                {(isAdmin||isLider) && (
                  <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                    <button onClick={() => navigate(`/dashboard/staff/${s.id}/editar`)} className="soem-btn-secondary flex-1 py-1.5 text-xs flex items-center justify-center gap-1">
                      <Edit2 size={12}/> Editar
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {modal !== null && <StaffModal staff={modal?.id ? modal : null} onSave={handleSave} onClose={() => setModal(null)} />}
    </div>
  );
}
